/**
 * Scroll-triggered section reveals — parity with Vite/Framer (whileInView + once).
 * Uses Shopify's .shopify-section wrappers (correct layout + height) and runs after
 * layout/paint so below-the-fold blocks don't all measure as "in view".
 *
 * Theme editor: re-inits on shopify:section:load / shopify:section:reorder so replaced
 * sections (and [data-cosmic-reveal] nodes) are not stuck at opacity:0.
 */
(function () {
  var reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {IntersectionObserver | null} */
  var sectionObserver = null;
  /** @type {IntersectionObserver | null} */
  var revealObserver = null;

  function getSectionNodes() {
    var main = document.getElementById('MainContent');
    if (!main) return [];
    var direct = main.querySelectorAll(':scope > .shopify-section');
    if (direct.length) {
      return Array.prototype.slice.call(direct);
    }
    return Array.prototype.slice.call(main.querySelectorAll('section'));
  }

  function markAndObserve() {
    if (reduceMq.matches) return;
    if (sectionObserver) {
      try {
        sectionObserver.disconnect();
      } catch (e) {}
      sectionObserver = null;
    }

    var wrappers = getSectionNodes();
    if (!wrappers.length) return;

    var vh = window.innerHeight;
    var foldRatio = 0.88;

    wrappers.forEach(function (el, index) {
      el.classList.add('cosmic-animate-surface');

      if (el.querySelector('[data-cosmic-reveal]')) {
        el.classList.add('cosmic-animate-surface--flat');
        el.classList.add('cosmic-animate--inview');
        return;
      }

      if (index === 0) {
        el.classList.add('cosmic-animate--inview');
        return;
      }

      var top = el.getBoundingClientRect().top;
      if (top < vh * foldRatio) {
        el.classList.add('cosmic-animate--inview');
      } else {
        el.classList.add('cosmic-animate-await');
      }
    });

    var pending = wrappers.filter(function (el) {
      return el.classList.contains('cosmic-animate-await');
    });
    if (!pending.length) return;

    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('cosmic-animate--inview');
          entry.target.classList.remove('cosmic-animate-await');
          obs.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -6% 0px', threshold: [0, 0.01, 0.1] }
    );
    sectionObserver = obs;

    pending.forEach(function (el) {
      obs.observe(el);
    });
  }

  function onReduceMediaChange() {
    if (!reduceMq.matches) return;
    getSectionNodes().forEach(function (el) {
      el.classList.add('cosmic-animate--inview');
      el.classList.remove('cosmic-animate-await');
    });
    if (sectionObserver) {
      try {
        sectionObserver.disconnect();
      } catch (e) {}
      sectionObserver = null;
    }
    document.querySelectorAll('[data-cosmic-reveal]').forEach(function (el) {
      el.classList.add('cosmic-reveal--in');
    });
    if (revealObserver) {
      try {
        revealObserver.disconnect();
      } catch (e) {}
      revealObserver = null;
    }
  }

  if (reduceMq.addEventListener) {
    reduceMq.addEventListener('change', onReduceMediaChange);
  } else if (reduceMq.addListener) {
    reduceMq.addListener(onReduceMediaChange);
  }

  /**
   * After a section is re-rendered in the theme editor, force its wrapper + all
   * [data-cosmic-reveal] children visible so the iframe doesn't look empty.
   */
  function forceRevealShopifySection(sectionEl) {
    if (!sectionEl || !sectionEl.classList) return;
    if (!sectionEl.classList.contains('shopify-section')) return;

    sectionEl.classList.add('cosmic-animate-surface');
    if (sectionEl.querySelector('[data-cosmic-reveal]')) {
      sectionEl.classList.add('cosmic-animate-surface--flat', 'cosmic-animate--inview');
    } else {
      sectionEl.classList.add('cosmic-animate--inview');
    }
    sectionEl.classList.remove('cosmic-animate-await');

    sectionEl.querySelectorAll('[data-cosmic-reveal]').forEach(function (node) {
      node.classList.add('cosmic-reveal--in');
    });
  }

  function getSectionElementFromEvent(ev) {
    if (ev && ev.target && ev.target.classList && ev.target.classList.contains('shopify-section')) {
      return ev.target;
    }
    var id = ev && ev.detail && ev.detail.sectionId;
    if (id) {
      return document.getElementById('shopify-section-' + id);
    }
    return null;
  }

  /**
   * CoffeePlusBenefits.tsx: whileInView once on L/R columns ([data-cosmic-reveal])
   * Idempotent: disconnect previous observer, skip nodes that already have .cosmic-reveal--in
   */
  function initCosmicDataReveals() {
    if (revealObserver) {
      try {
        revealObserver.disconnect();
      } catch (e) {}
      revealObserver = null;
    }

    if (reduceMq.matches) {
      document.querySelectorAll('[data-cosmic-reveal]').forEach(function (el) {
        el.classList.add('cosmic-reveal--in');
      });
      return;
    }

    var nodes = document.querySelectorAll('[data-cosmic-reveal]');
    if (!nodes.length) return;

    function reveal(el) {
      el.classList.add('cosmic-reveal--in');
    }

    function inFold(el) {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      return r.top < vh * 0.92 && r.bottom > -80;
    }

    var revObs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          revObs.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: [0, 0.08] }
    );
    revealObserver = revObs;

    Array.prototype.forEach.call(nodes, function (el) {
      if (el.classList.contains('cosmic-reveal--in')) return;
      if (inFold(el)) {
        reveal(el);
      } else {
        revObs.observe(el);
      }
    });
  }

  function runAfterLayout() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (reduceMq.matches) {
          document.querySelectorAll('[data-cosmic-reveal]').forEach(function (el) {
            el.classList.add('cosmic-reveal--in');
          });
        } else {
          markAndObserve();
          initCosmicDataReveals();
        }
      });
    });
  }

  if (document.readyState === 'complete') {
    runAfterLayout();
  } else {
    window.addEventListener('load', runAfterLayout);
  }

  function onSectionLifecycle(ev) {
    var sec = getSectionElementFromEvent(ev);
    if (sec) {
      forceRevealShopifySection(sec);
    }
    runAfterLayout();
  }

  document.addEventListener('shopify:section:load', onSectionLifecycle);
  document.addEventListener('shopify:section:reorder', onSectionLifecycle);
})();
